import { colors } from "./colors";

/**
 * สถานะคำร้องคืนค่ามัดจำแผง — ตรงตาม spec (deposit-refund-api.md ข้อ 2)
 * DELETED ไม่เคยคืนมาใน API (soft delete) จึงไม่มีในนี้
 */
export enum DEPOSIT_REFUND_STATUS {
  WAITING = "WAITING",
  APPROVE = "APPROVE",
  NOT_APPROVE = "NOT_APPROVE",
  CANCELED = "CANCELED",
}

/** ค่าพิเศษของตัวกรอง ไม่ได้ส่งไป backend (ไม่ส่ง status = ทั้งหมด) */
export const DEPOSIT_REFUND_STATUS_ALL = "ALL";

/** ค่าพิเศษของตัวกรองโซน/แผงค้า ไม่ได้ส่งไป backend */
export const DEPOSIT_REFUND_FILTER_ALL = "ALL";

/**
 * label ฝั่งเจ้าหน้าที่ (เมนู "คืนค่ามัดจำแผง")
 * ห้ามใช้ t('STATUS.WAITING') จาก common.json เพราะที่นั่นแปลว่า "รอการชำระเงิน"
 */
export const DEPOSIT_REFUND_STATUS_LABEL: Record<string, string> = {
  [DEPOSIT_REFUND_STATUS.WAITING]: "รอพิจารณา",
  [DEPOSIT_REFUND_STATUS.APPROVE]: "อนุมัติ",
  [DEPOSIT_REFUND_STATUS.NOT_APPROVE]: "ไม่อนุมัติ",
  [DEPOSIT_REFUND_STATUS.CANCELED]: "ยกเลิกคำร้อง",
  [DEPOSIT_REFUND_STATUS_ALL]: "ทั้งหมด",
};

/**
 * label ฝั่งลูกค้า — ต่างจากฝั่งเจ้าหน้าที่แค่ WAITING (บอกว่ารอ "ใคร")
 * spread แล้วทับ key เดียว ไม่เขียน map ซ้ำสองชุด เพื่อไม่ให้หลุดกันเมื่อเพิ่มสถานะใหม่
 */
export const DEPOSIT_REFUND_STATUS_LABEL_CUSTOMER: Record<string, string> = {
  ...DEPOSIT_REFUND_STATUS_LABEL,
  [DEPOSIT_REFUND_STATUS.WAITING]: "รอเจ้าหน้าที่พิจารณา",
};

/** สีตัวอักษรและจุดนำหน้าใน dropdown (ใช้ palette เดิมจาก constants/colors) */
export const DEPOSIT_REFUND_STATUS_COLOR: Record<string, string> = {
  [DEPOSIT_REFUND_STATUS.WAITING]: colors.lightOrange,
  [DEPOSIT_REFUND_STATUS.APPROVE]: colors.green,
  [DEPOSIT_REFUND_STATUS.NOT_APPROVE]: colors.red,
  [DEPOSIT_REFUND_STATUS.CANCELED]: colors.gray,
  [DEPOSIT_REFUND_STATUS_ALL]: colors.gray,
};

/** พื้นหลัง chip ในตาราง — โทนอ่อนของสีเดียวกัน ให้ตัวอักษรยังอ่านออก */
export const DEPOSIT_REFUND_STATUS_BG: Record<string, string> = {
  [DEPOSIT_REFUND_STATUS.WAITING]: "rgba(240, 183, 96, 0.18)",
  [DEPOSIT_REFUND_STATUS.APPROVE]: "rgba(28, 185, 154, 0.14)",
  [DEPOSIT_REFUND_STATUS.NOT_APPROVE]: "rgba(176, 0, 32, 0.10)",
  [DEPOSIT_REFUND_STATUS.CANCELED]: "rgba(108, 117, 125, 0.12)",
};

/** ลำดับที่ใช้ render dropdown ตัวกรองสถานะ */
export const DEPOSIT_REFUND_STATUS_OPTIONS: string[] = [
  DEPOSIT_REFUND_STATUS_ALL,
  DEPOSIT_REFUND_STATUS.WAITING,
  DEPOSIT_REFUND_STATUS.APPROVE,
  DEPOSIT_REFUND_STATUS.NOT_APPROVE,
  DEPOSIT_REFUND_STATUS.CANCELED,
];

/** ผลการพิจารณาที่เลือกได้ใน modal พิจารณา (มีแค่ 2 ค่าตาม spec) */
export const DEPOSIT_REFUND_REVIEW_OPTIONS: string[] = [
  DEPOSIT_REFUND_STATUS.APPROVE,
  DEPOSIT_REFUND_STATUS.NOT_APPROVE,
];

/** สถานะที่ยังไม่รู้จัก ให้โชว์ค่าดิบไว้ก่อน ดีกว่าโชว์ "-" แล้วข้อมูลหายไปเฉยๆ */
export const depositRefundStatusLabel = (status?: string, isCustomer = false) => {
  const labels = isCustomer
    ? DEPOSIT_REFUND_STATUS_LABEL_CUSTOMER
    : DEPOSIT_REFUND_STATUS_LABEL;
  return (status && labels[status]) || status || "-";
};

export const depositRefundStatusColor = (status?: string) =>
  (status && DEPOSIT_REFUND_STATUS_COLOR[status]) || colors.gray;

export const depositRefundStatusBg = (status?: string) =>
  (status && DEPOSIT_REFUND_STATUS_BG[status]) || colors.extraLightGray;

/**
 * สีของสถานะสัญญาเช่าในตารางเลือกสัญญา
 * ป้ายข้อความใช้ lease_status_name ที่ backend ส่งมา ไม่ต้อง map เอง
 */
export const LEASE_STATUS_COLOR: Record<string, string> = {
  ACTIVE: colors.green,
  INACTIVE: colors.gray,
  CANCEL: colors.red,
  TRANSFER: colors.lightOrange,
};

export const leaseStatusColor = (status?: string) =>
  (status && LEASE_STATUS_COLOR[status]) || colors.gray;

/** ป้ายของรายการใน log */
export const DEPOSIT_REFUND_LOG_ACTION_LABEL: Record<string, string> = {
  CREATE: "ยื่นคำร้อง",
  REVIEW: "พิจารณาคำร้อง",
  CANCEL: "ยกเลิกคำร้อง",
  DELETE: "ลบคำร้อง",
};

/** ไฟล์แนบตอนพิจารณา — สูงสุด 10 ไฟล์ ไฟล์ละไม่เกิน 10 MB (spec ข้อ 3.8) */
export const DEPOSIT_REFUND_REVIEW_MAX_FILES = 10;
export const DEPOSIT_REFUND_REVIEW_MAX_FILE_SIZE = 10 * 1024 * 1024;
/** react-dropzone 14 ต้องใช้ accept แบบ object และให้ accept เป็นตัวคัดกรองเดียว */
export const DEPOSIT_REFUND_REVIEW_ACCEPT = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};
/** note ≤ 5000 ตัวอักษร, doc_ref ≤ 100 (spec ข้อ 3.8) */
export const DEPOSIT_REFUND_REVIEW_MAX_NOTE_LENGTH = 5000;
export const DEPOSIT_REFUND_DOC_REF_MAX_LENGTH = 100;

/**
 * ช่องทางการคืนเงิน — ใช้ทำ dropdown ใน modal พิจารณา (spec v2 ข้อ 3.1)
 * เวลา "แสดง" ค่าที่บันทึกแล้วให้ใช้ refund_method_name ที่ backend ส่งมา ไม่ต้อง map เอง
 * map ตัวนี้มีไว้เพื่อทำ option ตอนกรอกเท่านั้น
 */
export const DEPOSIT_REFUND_METHOD_LABEL: Record<string, string> = {
  BANK_TRANSFER: "โอนเข้าบัญชีธนาคาร",
  CASH: "เงินสด",
  CHEQUE: "เช็คธนาคาร",
};

export const DEPOSIT_REFUND_METHOD_OPTIONS: string[] = [
  "BANK_TRANSFER",
  "CASH",
  "CHEQUE",
];

export const depositRefundMethodLabel = (method?: string | null) =>
  (method && DEPOSIT_REFUND_METHOD_LABEL[method]) || method || "-";

/**
 * ตารางใบเสร็จในโมดัลไม่มี pagination จึงดึงมาทีเดียว — backend clamp เพดานไว้ที่ 100
 * ส่งเกินก็ได้แค่ 100 (ดู deposit-refund-receipt-api.md ข้อ 4)
 */
export const DEPOSIT_REFUND_RECEIPT_LIMIT = 100;
